<template>
  <div>
    <loader v-if="loading && !doEsign" :height="500" />
    <template v-if="invoices.length > 0 && !loading && !doEsign">
      <b-row>
        <b-col :cols="12" :md="9">
          <div
            class="d-flex align-items-center justify-content-between flex-md-row flex-column align-items-right text-center"
          >
            <h1 class="card-title mb-0 py-4">
              {{ $t('invoices.esign.title') }}
            </h1>
            <div class="mb-4 mb-md-0">
              <span
                class="d-inline-block"
                tabindex="0"
                v-b-tooltip.top
                :title="hasPermission ? '' : $t('invoices.esign.toolTip')"
              >
                <b-button
                  @click="handleEsign"
                  variant="secondary"
                  v-track-click
                  id="invoices-esign-now"
                  :disabled="!hasPermission"
                  >{{ $t('invoices.esign.esignNow') }}</b-button
                >
              </span>
              <nps-modal :showModal="isModalVisible" @close="closeModal" />
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col :cols="12" :md="9">
          <drip-card class="overflow-hidden esign-content">
            <div class="drip-table-container">
              <div class="table-display">
                <b-table
                  class="drip-table table-borderless"
                  :items="invoices"
                  v-if="invoices.length > 0"
                  :fields="fields"
                >
                  <template v-slot:cell(reference)="data">
                    <div class="d-flex align-items-center">
                      {{ data.item.reference }}
                      <remittance-flag-chip
                        :isEnabaled="data.item.drip_switch_enabled"
                        v-if="isIndia"
                      />
                    </div>
                  </template>
                  <template v-slot:cell(value)="data">
                    {{ data.value | currency(data.item.currsym) }}
                  </template>
                </b-table>
              </div>
            </div>
          </drip-card>
        </b-col>
        <b-col>
          <rate-conversion-widget
            :rate="conversionRatesData.rate"
            :isFulfilled="isRateConversionApiFulfilled"
            v-if="isIndia"
            :isINREnabled="isDripSwitchEnabled"
          />
        </b-col>
      </b-row>
    </template>
    <template v-if="invoices.length === 0 && !loading && !doEsign">
      <h1 class="page-title section-title">
        {{ $t('invoices.esign.noDocs') }}
      </h1>
      <b-button
        :to="{ name: 'invoices' }"
        variant="secondary"
        v-track-click
        id="invoices-esign-view-all"
        >{{ $t('invoices.esign.viewAll') }}</b-button
      >
    </template>
    <loader v-if="doEsign" :height="500" />
  </div>
</template>
<script>
import Loader from '@/components/common/Loader'
import DripCard from '@/components/drip/ui/Card/'
import NpsModal from '@/components/common/NpsModal/'
import { freshdeskMixin } from '@/mixins/Freshdesk'
import invoiceService from '@/services/invoice'
import accountService from '@/services/accounts'
import exportersService from '@/services/exporters'
import RemittanceFlagChip from '@/components/common/RemittanceFlagChip'
import conversionRateService from '@/services/conversionRate'
import {
  RATE_CONVERSION_REFETCH_DEFAULT_TIME,
  MAXIMUM_SET_TIMEOUT_DELAY
} from '@/utils/constants'
import moment from 'moment'
import RateConversionWidget from '@/components/dashboard/RateConversionWidget'

export default {
  data() {
    return {
      loading: true,
      esignUrl: null,
      doEsign: false,
      invoices: [],
      fields: this.$t('invoices.esign.fields'),
      isModalVisible: false,
      conversionRatesData: {
        rate: '',
        from_currency: '',
        to_currency: '',
        valid_till: ''
      },
      timeOutID: '',
      isRateConversionApiFulfilled: false,
      isDripSwitchEnabled: null,
      docId: null
    }
  },
  beforeCreate() {
    const toDashboard = !(
      this.$store.getters.hasPermission('esign') ||
      this.$store.getters.hasPermission('edit') ||
      this.$store.getters.hasInvoiceAddAndCreatePermission()
    )
    /**
       * ?: below is the old access roles code
        return (
          this.$store.getters.hasPermission('esign') ||
          this.$store.getters.hasPermission('edit')
        )
       **/

    if (toDashboard) {
      this.$router.push('/')
    }
  },
  mounted() {
    document.title = 'Esign Invoices | Drip Portal'
    this.$mpTrack('Esign Invoices page mounted')
    this.fetchUser()
    this.generateEsign()
    if (this.$store.state.account.country === 'India') {
      this.showAndPrefillFreshdesk({ cf_category_i: 'Invoice related' })
    }
    this.getCoversionRate()
  },
  computed: {
    hasPermission() {
      return this.$store.getters.hasPermission('esign')
    },
    invoicesUrl() {
      return process.env.VUE_APP_PORTAL_ESIGN_REDIRECT_URL
    },
    isIndia() {
      return this.$store.state.account.country === 'India'
    },
    isCoversionApiSuccess() {
      return this.isRateConversionApiFulfilled && this.conversionRatesData.rate
    }
  },
  methods: {
    generateEsign() {
      return exportersService
        .fetchExportersAadhaarSignableDocs(
          this.$store.state.account.accountId,
          !this.$i18n.locale.includes('MX') && this.$route.query.id
            ? {
                vars: { invoice_id: this.$route.query.id }
              }
            : undefined,
          this.$i18n.locale.includes('MX')
        )
        .then((res) => {
          this.getSignableInvoices()
        })
        .catch()
    },
    getSignableInvoices() {
      return exportersService
        .fetchExporterMetaData(this.$store.state.account.accountId, {
          include: ['generated_invoices']
        })
        .then((res) => {
          this.invoices = res.data.generated_invoices
          this.isDripSwitchEnabled = res.data.drip_switch_enabled
          if (this.invoices.length > 0) {
            this.getEsignUrl()
          } else {
            this.loading = false
          }
        })
        .catch()
    },
    getEsignUrl() {
      return invoiceService
        .fetchAbodeSign({
          vars: {
            account_id: this.$store.state.account.accountId,
            user_id: this.$store.getters.user.id,
            post_redirect: this.invoicesUrl,
            clickwrap: true
          }
        })
        .then((res) => {
          this.loading = false
          this.esignUrl = res.data.redirect_url
          this.docId = res.data.id
        })
        .catch()
    },
    handleEsign() {
      if (this.$store.getters.npsEnabled) {
        this.showModal()
      } else {
        this.$mpTrack(`Esign Page - navigate to Drip Esign`)
        if (this.docId) {
          this.$router.push({
            name: 'dripSign',
            query: {
              docId: this.docId
            }
          })
        } else {
          this.$notify({
            title: 'Could not generate document for e-signing!',
            type: 'error'
          })
        }
      }
    },
    showModal() {
      this.isModalVisible = true
    },
    closeModal() {
      this.isModalVisible = false
      this.doEsign = true
    },
    fetchUser() {
      return accountService
        .fetchLoggedUser(this.$store.getters.token)
        .then((res) => {
          const currentUser = res.data.linked_users.filter((user) => {
            return user.id === this.$store.state.account.accountId
          })[0]
          this.$store.dispatch('setAccount', {
            account: currentUser
          })
        })
    },
    async getCoversionRate() {
      this.isRateConversionApiFulfilled = false
      const exporterId = this.findExporter(
        this.$store.state.account.accountId
      ).id
      try {
        const { data } = await conversionRateService.getConversionRate(
          exporterId
        )
        this.conversionRatesData = data
        this.isRateConversionApiFulfilled = true
        let timeLeft = 0
        timeLeft = moment(this.conversionRatesData.valid_till).diff(new Date())
        let bufferAddedToTimeLeft = timeLeft + 100 // 100ms buffer added before making another API call so that we always fetch the newer data
        if (
          !bufferAddedToTimeLeft ||
          bufferAddedToTimeLeft < 0 ||
          bufferAddedToTimeLeft > MAXIMUM_SET_TIMEOUT_DELAY
        ) {
          bufferAddedToTimeLeft = RATE_CONVERSION_REFETCH_DEFAULT_TIME
        }
        clearTimeout(this.timeOutID)
        this.timeOutID = setTimeout(() => {
          this.getCoversionRate()
        }, bufferAddedToTimeLeft)
      } catch (err) {
        this.isRateConversionApiFulfilled = true
        clearTimeout(this.timeOutID)
        this.timeOutID = setTimeout(() => {
          this.getCoversionRate()
        }, RATE_CONVERSION_REFETCH_DEFAULT_TIME)
        console.log(err)
      }
    },
    findExporter(exporterId) {
      return this.$store.getters.user.linked_users.find((exporter) => {
        return parseInt(exporter.id) === parseInt(exporterId)
      })
    }
  },
  components: {
    Loader,
    DripCard,
    NpsModal,
    RemittanceFlagChip,
    RateConversionWidget
  },
  mixins: [freshdeskMixin],
  watch: {
    doEsign(newValue) {
      if (newValue) {
        this.$router.push({
          name: 'dripSign',
          query: {
            docId: this.docId
          }
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.esign-content {
  height: calc(100vh - 200px);
}
</style>
